import { Building2, ExternalLink, Users } from "lucide-react";
import { formatCurrencyEUR, formatRelative } from "@/lib/utils";
import type { Account } from "./use-admin-deal";
import { LinkAccountButton } from "./link-account-button";

// SPA port of AccountPanel. Renders the cached HubSpot Company snapshot
// attached to a HubSpot Sales-pipeline deal (the "target customer").
// Internal users see a Link/Unlink affordance; partner users get a
// read-only view (canLink defaults to false from the drawer, which is
// internal-only anyway under sub-bucket 16).

export function AccountPanel({
  account,
  hubspotDealId,
  canLink,
}: {
  account: Account | null;
  hubspotDealId: string;
  canLink: boolean;
}) {
  if (!account) {
    return (
      <section
        aria-label="Target customer"
        className="rounded-lg border border-dashed bg-muted/30 px-4 py-5"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="font-medium text-foreground">No account linked</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Link a HubSpot Company as the target customer for this deal.
              </p>
            </div>
          </div>
          {canLink && (
            <LinkAccountButton hubspotDealId={hubspotDealId} mode="link" />
          )}
        </div>
      </section>
    );
  }

  const location = [account.location_city, account.location_country]
    .filter(Boolean)
    .join(", ");

  return (
    <section
      aria-label="Target customer"
      className="rounded-lg border bg-card px-4 py-4"
    >
      <header className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Target customer
          </p>
          <h3 className="mt-0.5 truncate font-display text-base tracking-tight text-foreground">
            {account.name ?? "Unnamed company"}
          </h3>
          {account.industry && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {account.industry}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <a
            href={account.hubspot_url}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium text-accent hover:bg-accent/10"
            title="Open in HubSpot"
          >
            <ExternalLink className="h-3 w-3" />
            HubSpot
          </a>
          {canLink && (
            <LinkAccountButton
              hubspotDealId={hubspotDealId}
              mode="relink"
              currentHubspotId={account.hubspot_company_id}
            />
          )}
        </div>
      </header>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-xs">
        {account.website && (
          <Field label="Website">
            <a
              href={
                account.website.startsWith("http")
                  ? account.website
                  : `https://${account.website}`
              }
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-accent hover:underline"
            >
              {account.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
              <ExternalLink className="h-3 w-3" />
            </a>
          </Field>
        )}
        {location && <Field label="Location">{location}</Field>}
        {account.employee_count != null && (
          <Field label="Employees">
            <span className="inline-flex items-center gap-1">
              <Users className="h-3 w-3 text-muted-foreground" />
              {account.employee_count.toLocaleString("en-GB")}
            </span>
          </Field>
        )}
        {account.annual_revenue != null && (
          <Field label="Annual revenue">
            {formatCurrencyEUR(account.annual_revenue)}
          </Field>
        )}
      </dl>

      {account.description && (
        <p className="mt-3 line-clamp-3 border-t pt-3 text-xs text-muted-foreground">
          {account.description}
        </p>
      )}

      <footer className="mt-3 flex items-center justify-between gap-2 border-t pt-2 text-[10px] text-muted-foreground">
        <span>HubSpot · synced {formatRelative(account.last_synced_at)}</span>
        {canLink && (
          <LinkAccountButton hubspotDealId={hubspotDealId} mode="unlink" />
        )}
      </footer>
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 truncate text-foreground">{children}</dd>
    </div>
  );
}
